import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Shield } from 'lucide-react';
import { BuildStep } from '../../stores/useBuildStacksStore.js';
import { Button } from '../ui/button.js';

interface BuildStackStepEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (step: BuildStep) => void;
  initialStep?: BuildStep;
}

const mockBeeEntries = [
  { name: 'git', capabilities: ['clone', 'commit', 'push'] },
  { name: 'npm', capabilities: ['install', 'build', 'test'] },
  { name: 'docker', capabilities: ['build', 'run', 'push'] },
  { name: 'deploy', capabilities: ['staging', 'production'] },
  { name: 'test', capabilities: ['unit', 'integration', 'e2e'] },
  { name: 'lint', capabilities: ['check', 'fix'] },
  { name: 'arcSecurity', capabilities: ['scan', 'validate'] }
];

const BuildStackStepEditor: React.FC<BuildStackStepEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  initialStep
}) => {
  const [beeName, setBeeName] = useState('');
  const [command, setCommand] = useState('');
  const [inputs, setInputs] = useState('{}');
  const [inputsError, setInputsError] = useState('');

  useEffect(() => {
    if (initialStep) {
      setBeeName(initialStep.beeName);
      setCommand(initialStep.command);
      setInputs(JSON.stringify(initialStep.inputs || {}, null, 2));
    } else {
      setBeeName(mockBeeEntries[0]?.name || '');
      setCommand('');
      setInputs('{}');
    }
    setInputsError('');
  }, [initialStep, isOpen]);

  const handleSave = () => {
    if (!beeName.trim()) {
      return;
    }
    if (!command.trim()) {
      return;
    }

    try {
      const parsedInputs = JSON.parse(inputs);
      const step: BuildStep = {
        beeName: beeName.trim(),
        command: command.trim(),
        inputs: parsedInputs
      };
      onSave(step);
      onClose();
    } catch {
      setInputsError('Invalid JSON format');
    }
  };

  const handleInputsChange = (value: string) => {
    setInputs(value);
    setInputsError('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center" onKeyDown={handleKeyDown}>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />
        <motion.div 
          className="relative bg-slate-800 border border-slate-600 rounded-lg max-w-lg w-full mx-4 shadow-3xl"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">
                {initialStep ? 'Edit Step' : 'Add Step'}
              </h2>
              <motion.button
                onClick={onClose}
                className="text-slate-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X size={20} />
              </motion.button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Bee <span className="text-red-400">*</span>
                </label>
                <select
                  value={beeName}
                  onChange={(e) => setBeeName(e.target.value)}
                  className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a bee...</option>
                  {mockBeeEntries.map((bee) => (
                    <option key={bee.name} value={bee.name}>
                      {bee.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Command <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={command}
                  onChange={(e) => setCommand(e.target.value)}
                  className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter command name..."
                  required
                />
                {beeName && mockBeeEntries.find(b => b.name === beeName) && (
                  <div className="mt-1 text-xs text-slate-400">
                    Available commands: {mockBeeEntries.find(b => b.name === beeName)?.capabilities.join(', ')}
                    {beeName === 'arcSecurity' && (
                      <div className="flex items-center gap-1 mt-1 text-green-400">
                        <Shield className="w-3 h-3" />
                        <span>Security scanning and file validation</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Inputs (JSON)
                </label>
                <textarea
                  value={inputs}
                  onChange={(e) => handleInputsChange(e.target.value)}
                  rows={6}
                  className={`w-full p-3 bg-slate-700 border rounded-lg text-white focus:ring-1 font-mono text-sm ${
                    inputsError 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                      : 'border-slate-600 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  placeholder='{"key": "value"}'
                />
                {inputsError && (
                  <p className="text-red-400 text-sm mt-1">{inputsError}</p>
                )}
                <div className="mt-1 text-xs text-slate-400">
                  Use valid JSON format. Empty object {} is acceptable.
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                onClick={onClose}
                variant="ghost"
                className="text-slate-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!beeName.trim() || !command.trim()}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white disabled:bg-slate-600 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                Save Step
              </Button>
            </div>

            <div className="mt-4 text-xs text-slate-500 text-center">
              Press Ctrl+Enter to save, Escape to cancel
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BuildStackStepEditor;
